// La page Recrutement : boîte de réception des candidatures déposées sur la page
// « Offres d'emploi » du site public. L'admin est un LECTEUR des données
// Cloudflare (table D1 + bucket R2 dédiés, alimentés par le worker public) :
// la liste se rafraîchit d'elle-même, les documents sont streamés à la demande.
// Les notes internes, l'épingle et le statut vivent dans le SQL admin
// (annotations, sauvegardées) ; la seule écriture Cloudflare est la suppression
// définitive (ligne + PDF), gardée par une double confirmation.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
	FiBriefcase,
	FiDownload,
	FiEdit3,
	FiExternalLink,
	FiInbox,
	FiMail,
	FiPhone,
	FiRefreshCw,
	FiSearch,
	FiStar,
	FiTrash2
} from "react-icons/fi";
import { usePageHeader } from "../../hooks/usePageHeader";
import { Modal } from "../../Components/Modal";
import GatedButton from "../../Components/GatedButton";
import { formatExactDateFr, formatRelativeTimeFr } from "../../Helper/dates";
import { buildPreviewUrl } from "../../Helper/siteLinks";
import {
	careersClient,
	type CareerApplication,
	type CareerApplicationStatus,
	type CareerPosteOption
} from "./careersClient";

export const CAREERS_PAGE_SIZE = 25;
const WRITE_TOKEN = "feature:recrutement:write";
/** Rafraîchissement automatique : l'admin lit Cloudflare, les nouvelles
 *  candidatures apparaissent sans action manuelle. */
const AUTO_REFRESH_MS = 60_000;

const STATUS_TABS: Array<{ key: CareerApplicationStatus | ""; label: string }> = [
	{ key: "new", label: "Nouvelles" },
	{ key: "reviewed", label: "Examinées" },
	{ key: "shortlisted", label: "Retenues" },
	{ key: "rejected", label: "Écartées" },
	{ key: "", label: "Toutes" }
];

const STATUS_LABELS: Record<CareerApplicationStatus, { label: string; className: string }> = {
	new: { label: "Nouvelle", className: "border-[#005CA9]/30 bg-[#005CA9]/10 text-[#005CA9]" },
	reviewed: { label: "Examinée", className: "border-slate-200 bg-slate-50 text-slate-600" },
	shortlisted: { label: "Retenue", className: "border-emerald-200 bg-emerald-50 text-emerald-700" },
	rejected: { label: "Écartée", className: "border-rose-200 bg-rose-50 text-rose-600" }
};

const EnvPill: React.FC<{ env: CareerApplication["sourceEnv"] }> = ({ env }) =>
	env === "production" ? (
		<span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
			Production
		</span>
	) : (
		<span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-700">
			Preview
		</span>
	);

const StatusPill: React.FC<{ status: CareerApplicationStatus }> = ({ status }) => {
	const s = STATUS_LABELS[status];
	return (
		<span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${s.className}`}>
			{s.label}
		</span>
	);
};

const RowSkeleton: React.FC = () => (
	<div className="animate-pulse space-y-2 px-5 py-4">
		<div className="flex items-center gap-3">
			<div className="h-4 w-44 rounded bg-slate-100" />
			<div className="h-4 w-28 rounded-full bg-slate-100" />
			<div className="ml-auto h-3 w-20 rounded bg-slate-100" />
		</div>
		<div className="h-3 w-1/2 rounded bg-slate-100" />
	</div>
);

const formatMo = (size: number): string => `${(size / (1024 * 1024)).toFixed(1)} Mo`;

const Careers: React.FC = () => {
	usePageHeader({ title: "Recrutement" });

	const [status, setStatus] = useState<CareerApplicationStatus | "">("new");
	const [search, setSearch] = useState("");
	const [posteId, setPosteId] = useState("");
	const [postes, setPostes] = useState<CareerPosteOption[]>([]);
	const [page, setPage] = useState(0);
	const [items, setItems] = useState<CareerApplication[]>([]);
	const [total, setTotal] = useState<number | null>(null);
	const [hasMore, setHasMore] = useState(false);
	const [loading, setLoading] = useState(true);
	const [selected, setSelected] = useState<CareerApplication | null>(null);
	const [notesDraft, setNotesDraft] = useState("");
	const [notesSaving, setNotesSaving] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const searchDebounce = useRef<number | null>(null);
	const [effectiveSearch, setEffectiveSearch] = useState("");

	const loadList = useCallback(async () => {
		setLoading(true);
		try {
			const res = await careersClient.list({
				status: status || undefined,
				search: effectiveSearch || undefined,
				posteId: posteId || undefined,
				skip: page * CAREERS_PAGE_SIZE,
				take: CAREERS_PAGE_SIZE
			});
			setItems(res.items);
			setTotal(res.total);
			setHasMore(res.items.length === CAREERS_PAGE_SIZE);
		} catch {
			setItems([]);
			setHasMore(false);
			toast.error("Échec du chargement des candidatures.");
		} finally {
			setLoading(false);
		}
	}, [status, effectiveSearch, posteId, page]);

	const loadPostes = useCallback(async () => {
		try {
			setPostes(await careersClient.postes());
		} catch {
			/* transitoire : le filtre garde ses dernières options */
		}
	}, []);

	useEffect(() => {
		loadList();
	}, [loadList]);

	useEffect(() => {
		loadPostes();
	}, [loadPostes]);

	// Rafraîchissement automatique périodique (lecteur Cloudflare).
	useEffect(() => {
		const t = window.setInterval(() => {
			loadList();
			loadPostes();
		}, AUTO_REFRESH_MS);
		return () => window.clearInterval(t);
	}, [loadList, loadPostes]);

	// Recherche : appliquée avec un léger débounce pour ne pas mitrailler l'API.
	useEffect(() => {
		if (searchDebounce.current) window.clearTimeout(searchDebounce.current);
		searchDebounce.current = window.setTimeout(() => {
			setEffectiveSearch(search.trim());
			setPage(0);
		}, 350);
		return () => {
			if (searchDebounce.current) window.clearTimeout(searchDebounce.current);
		};
	}, [search]);

	const refresh = () => {
		loadList();
		loadPostes();
	};

	const openApplication = (application: CareerApplication) => {
		setSelected(application);
		setNotesDraft(application.notes);
		setConfirmDelete(false);
	};

	const closeModal = () => {
		setSelected(null);
		setConfirmDelete(false);
	};

	/** Applique une annotation (statut / épingle) et met la liste à jour localement. */
	const annotate = useCallback(
		async (application: CareerApplication, patch: { pinned?: boolean; status?: CareerApplicationStatus; notes?: string }) => {
			try {
				await careersClient.annotate(application.id, patch);
				const apply = (a: CareerApplication): CareerApplication => ({ ...a, ...patch });
				setItems((current) => current.map((a) => (a.id === application.id ? apply(a) : a)));
				setSelected((current) => (current && current.id === application.id ? apply(current) : current));
				return true;
			} catch {
				toast.error("Échec de l'enregistrement.");
				return false;
			}
		},
		[]
	);

	const saveNotes = async () => {
		if (!selected) return;
		setNotesSaving(true);
		const ok = await annotate(selected, { notes: notesDraft });
		setNotesSaving(false);
		if (ok) toast.success("Note enregistrée.");
	};

	const togglePin = async (application: CareerApplication) => {
		const ok = await annotate(application, { pinned: !application.pinned });
		if (ok) toast.success(application.pinned ? "Épingle retirée." : "Candidature épinglée.");
	};

	const removeApplication = async () => {
		if (!selected) return;
		try {
			await careersClient.remove(selected.id);
			toast.success("Candidature supprimée (données et documents Cloudflare inclus).");
			closeModal();
			loadList();
			loadPostes();
		} catch {
			toast.error("Échec de la suppression.");
		}
	};

	// Épinglées d'abord, puis ordre du serveur (déjà anti-chronologique).
	const orderedItems = useMemo(() => {
		return [...items].sort((a, b) => Number(b.pinned) - Number(a.pinned));
	}, [items]);

	return (
		<div className="mx-auto max-w-6xl space-y-4 p-6">
			{/* ---- En-tête : titre, rafraîchir, onglets + filtres ---- */}
			<header className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
				<div className="flex flex-wrap items-center gap-3">
					<div className="min-w-0">
						<p className="text-xs uppercase tracking-widest text-slate-500">Engagement</p>
						<h1 className="text-2xl font-semibold text-slate-900">Recrutement</h1>
						<p className="text-sm text-slate-500">
							Les candidatures déposées sur la page Offres d'emploi. Les postes se gèrent dans Contenus (type «
							Offre d'emploi »).
						</p>
					</div>
					<div className="ml-auto flex flex-wrap items-center gap-2">
						<a
							href={buildPreviewUrl("la-banque/carrieres/offres")}
							target="_blank"
							rel="noopener noreferrer"
							title="Fonctionnalité masquée du parcours public : accès de démonstration uniquement"
							className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3.5 py-2 text-sm font-semibold text-amber-800 shadow-sm transition hover:border-amber-400 hover:bg-amber-100"
						>
							<FiExternalLink className="h-4 w-4" /> Aperçu des offres · démo
						</a>
						{total !== null && (
							<span className="inline-flex items-center gap-1.5 rounded-full border border-[#005CA9]/30 bg-[#005CA9]/10 px-3 py-1.5 text-sm font-semibold text-[#005CA9]">
								<FiBriefcase className="h-3.5 w-3.5" />
								{total.toLocaleString("fr-FR")} candidature{total > 1 ? "s" : ""}
							</span>
						)}
						<button
							type="button"
							onClick={refresh}
							title="Rafraîchir"
							className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
						>
							<FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
						</button>
					</div>
				</div>

				<div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
					<div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5 shadow-sm">
						{STATUS_TABS.map((tab) => (
							<button
								key={tab.key || "all"}
								type="button"
								onClick={() => {
									setStatus(tab.key);
									setPage(0);
								}}
								className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition ${
									status === tab.key ? "bg-slate-900 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
								}`}
							>
								{tab.label}
							</button>
						))}
					</div>
					<div className="relative">
						<FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
						<input
							type="search"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Filtrer par nom ou prénom"
							className="w-56 rounded-full border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 shadow-sm outline-none transition focus:border-[#005CA9]/50 focus:ring-2 focus:ring-[#005CA9]/15"
						/>
					</div>
					<select
						value={posteId}
						onChange={(e) => {
							setPosteId(e.target.value);
							setPage(0);
						}}
						className="rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-600 shadow-sm outline-none transition focus:border-[#005CA9]/50"
					>
						<option value="">Tous les postes</option>
						{postes.map((p) => (
							<option key={p.posteId} value={p.posteId}>
								{p.posteTitre} ({p.count})
							</option>
						))}
						{/* « Autres » = candidatures spontanées du site ; toujours proposé,
						    même avant la première (le GROUP BY ne la connaît pas encore). */}
						{!postes.some((p) => p.posteId === "autres") && (
							<option value="autres">Autres</option>
						)}
					</select>
				</div>
			</header>

			{/* ---- Liste ---- */}
			<div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
				{loading ? (
					<div className="divide-y divide-slate-100">
						<RowSkeleton />
						<RowSkeleton />
						<RowSkeleton />
						<RowSkeleton />
					</div>
				) : orderedItems.length === 0 ? (
					<div className="px-6 py-14 text-center">
						<span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
							<FiInbox className="h-6 w-6" />
						</span>
						<p className="mt-3 text-sm font-semibold text-slate-600">Aucune candidature ne correspond à ces filtres.</p>
						<p className="mt-1 text-xs text-slate-400">
							Les candidatures déposées sur le site public apparaîtront ici automatiquement.
						</p>
					</div>
				) : (
					<ul className="divide-y divide-slate-100">
						{orderedItems.map((a) => {
							const isNew = a.status === "new";
							return (
								<li key={a.id}>
									<button
										type="button"
										onClick={() => openApplication(a)}
										className="grid w-full grid-cols-1 gap-1 px-5 py-4 text-left transition hover:bg-slate-50 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-3"
									>
										<span className="flex min-w-0 flex-wrap items-center gap-2.5">
											{a.pinned && <FiStar className="h-4 w-4 shrink-0 fill-amber-400 text-amber-400" aria-label="Épinglée" />}
											{isNew && !a.pinned && (
												<span className="h-2 w-2 shrink-0 rounded-full bg-[#005CA9]" aria-label="Nouvelle" />
											)}
											<span className={`truncate text-sm ${isNew ? "font-bold text-slate-900" : "font-medium text-slate-700"}`}>
												{a.prenom} {a.nom}
											</span>
											<StatusPill status={a.status} />
											<EnvPill env={a.sourceEnv} />
											{a.documents.length > 0 && (
												<span className="text-xs text-slate-400">
													{a.documents.length} document{a.documents.length > 1 ? "s" : ""}
												</span>
											)}
										</span>
										<span className="text-xs text-slate-400 sm:text-right" title={formatExactDateFr(a.createdAt)}>
											{formatRelativeTimeFr(a.createdAt)}
										</span>
										<span className={`truncate text-sm sm:col-span-2 ${isNew ? "font-semibold text-slate-800" : "text-slate-500"}`}>
											{a.posteTitre}
											{a.domaine ? ` : ${a.domaine}` : ""}
										</span>
										{/* Première ligne de la motivation + de la note interne, tronquées : un survol rapide de la liste suffit pour se remémorer chaque profil. */}
										{a.motivation.trim() && (
											<span className="truncate text-xs italic text-slate-400 sm:col-span-2">
												« {a.motivation.split(/\r?\n/)[0].trim()} »
											</span>
										)}
										{a.notes.trim() && (
											<span className="flex min-w-0 items-center gap-1.5 text-xs font-medium text-amber-700 sm:col-span-2">
												<FiEdit3 className="h-3.5 w-3.5 shrink-0" aria-label="Note interne" />
												<span className="truncate">{a.notes.split(/\r?\n/)[0].trim()}</span>
											</span>
										)}
									</button>
								</li>
							);
						})}
					</ul>
				)}

				{/* ---- Pagination ---- */}
				<div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/60 px-5 py-3">
					<span className="text-xs text-slate-400">
						Page {(page + 1).toLocaleString("fr-FR")}
						{!loading &&
							` : ${orderedItems.length.toLocaleString("fr-FR")} candidature${orderedItems.length > 1 ? "s" : ""} affichée${orderedItems.length > 1 ? "s" : ""}`}
					</span>
					<div className="flex items-center gap-2">
						<button
							type="button"
							disabled={page === 0 || loading}
							onClick={() => setPage((p) => Math.max(0, p - 1))}
							className="rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm font-semibold text-slate-600 shadow-sm transition enabled:hover:border-slate-300 enabled:hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
						>
							Précédent
						</button>
						<button
							type="button"
							disabled={!hasMore || loading}
							onClick={() => setPage((p) => p + 1)}
							className="rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm font-semibold text-slate-600 shadow-sm transition enabled:hover:border-slate-300 enabled:hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
						>
							Suivant
						</button>
					</div>
				</div>
			</div>

			{/* ---- Détail ---- */}
			<Modal show={selected !== null} onHide={closeModal} size="lg" centered>
				{selected && (
					<>
						<Modal.Header closeButton>
							<Modal.Title>
								{selected.prenom} {selected.nom} : {selected.posteTitre}
							</Modal.Title>
						</Modal.Header>
						<Modal.Body>
							<div className="space-y-4">
								{/* Métadonnées candidat + candidature. */}
								<div className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4 sm:grid-cols-2">
									<div className="min-w-0">
										<p className="text-xs uppercase tracking-wide text-slate-400">Candidat</p>
										<p className="truncate text-sm font-semibold text-slate-800">
											{selected.prenom} {selected.nom}
										</p>
										<span className="mt-0.5 flex items-center gap-1.5">
											<FiMail className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
											<a href={`mailto:${selected.email}`} className="truncate text-sm text-[#005CA9] hover:underline">
												{selected.email}
											</a>
										</span>
										<span className="mt-0.5 flex items-center gap-1.5">
											<FiPhone className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
											<a
												href={`tel:${selected.telephone.replace(/\s+/g, "")}`}
												className="truncate text-sm text-slate-700 hover:text-[#005CA9]"
											>
												{selected.telephone}
											</a>
										</span>
									</div>
									<div>
										<p className="text-xs uppercase tracking-wide text-slate-400">Candidature</p>
										<p className="text-sm font-semibold text-slate-800">{selected.posteTitre}</p>
										{selected.domaine && <p className="text-xs text-slate-500">{selected.domaine}</p>}
										<span className="mt-1 inline-flex items-center gap-2">
											<StatusPill status={selected.status} />
											<EnvPill env={selected.sourceEnv} />
											<span className="text-xs text-slate-400" title={formatExactDateFr(selected.createdAt)}>
												{formatRelativeTimeFr(selected.createdAt)}
											</span>
										</span>
									</div>
								</div>

								{/* Motivation, sauts de ligne préservés. */}
								<div className="rounded-xl border border-slate-200 bg-white p-4">
									<p className="mb-1 text-xs uppercase tracking-wide text-slate-400">Motivation</p>
									<p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700">{selected.motivation}</p>
								</div>

								{/* Documents : streamés depuis le stockage Cloudflare via l'admin. */}
								<div className="rounded-xl border border-slate-200 bg-white p-4">
									<p className="mb-2 text-xs uppercase tracking-wide text-slate-400">Documents</p>
									{selected.documents.length === 0 ? (
										<p className="text-sm text-slate-500">Aucun document.</p>
									) : (
										<ul className="space-y-2">
											{selected.documents.map((doc) => (
												<li key={doc.index}>
													<a
														href={careersClient.documentUrl(selected.id, doc.index)}
														target="_blank"
														rel="noreferrer"
														className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-[#005CA9] transition hover:border-[#005CA9]/40 hover:bg-white"
													>
														<FiDownload className="h-4 w-4" />
														{doc.name}
														<span className="text-xs font-normal text-slate-400">{formatMo(doc.size)}</span>
													</a>
												</li>
											))}
										</ul>
									)}
								</div>

								{/* Notes internes : SQL admin, jamais visibles du candidat. */}
								<div className="rounded-xl border border-slate-200 bg-white p-4">
									<p className="mb-2 flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-400">
										<FiEdit3 className="h-3.5 w-3.5" /> Notes internes
									</p>
									<textarea
										value={notesDraft}
										onChange={(e) => setNotesDraft(e.target.value)}
										rows={3}
										maxLength={4000}
										placeholder="Impressions d'entretien, points à vérifier, suite à donner..."
										className="w-full rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-[#005CA9]/50 focus:bg-white focus:ring-2 focus:ring-[#005CA9]/15"
									/>
									<div className="mt-2 flex justify-end">
										<GatedButton
											token={WRITE_TOKEN}
											onClick={saveNotes}
											disabled={notesSaving || notesDraft === selected.notes}
											className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
										>
											{notesSaving ? "Enregistrement..." : "Enregistrer la note"}
										</GatedButton>
									</div>
								</div>

								{/* Zone de suppression : double confirmation, efface AUSSI Cloudflare. */}
								{confirmDelete && (
									<div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
										<p className="text-sm font-semibold text-rose-700">
											Supprimer définitivement cette candidature ?
										</p>
										<p className="mt-1 text-xs text-rose-600">
											La ligne Cloudflare et les {selected.documents.length} document
											{selected.documents.length > 1 ? "s" : ""} PDF seront effacés. Cette action est irréversible.
										</p>
										<div className="mt-3 flex gap-2">
											<GatedButton
												token={WRITE_TOKEN}
												onClick={removeApplication}
												className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700"
											>
												<FiTrash2 className="h-4 w-4" /> Oui, supprimer
											</GatedButton>
											<button
												type="button"
												onClick={() => setConfirmDelete(false)}
												className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
											>
												Annuler
											</button>
										</div>
									</div>
								)}
							</div>
						</Modal.Body>
						<Modal.Footer>
							<GatedButton
								token={WRITE_TOKEN}
								onClick={() => togglePin(selected)}
								className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
							>
								<FiStar className={`h-4 w-4 ${selected.pinned ? "fill-amber-400 text-amber-400" : ""}`} />
								{selected.pinned ? "Désépingler" : "Épingler"}
							</GatedButton>
							<select
								value={selected.status}
								onChange={(e) => annotate(selected, { status: e.target.value as CareerApplicationStatus })}
								className="rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-600 shadow-sm outline-none transition focus:border-[#005CA9]/50"
							>
								<option value="new">Nouvelle</option>
								<option value="reviewed">Examinée</option>
								<option value="shortlisted">Retenue</option>
								<option value="rejected">Écartée</option>
							</select>
							{!confirmDelete && (
								<GatedButton
									token={WRITE_TOKEN}
									onClick={() => setConfirmDelete(true)}
									className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-600 shadow-sm transition hover:border-rose-300 hover:bg-rose-50"
								>
									<FiTrash2 className="h-4 w-4" /> Supprimer
								</GatedButton>
							)}
							<button
								type="button"
								onClick={closeModal}
								className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
							>
								Fermer
							</button>
						</Modal.Footer>
					</>
				)}
			</Modal>
		</div>
	);
};

export default Careers;
